import React, { createContext, useEffect, useState, useContext, useCallback } from "react";
import { fetchWithAuth, addToken} from "../utils/fetchWithAuth";

// Create the context
const GameConfig = createContext();

// Create the provider component
export const GameConfigProvider = ({ children }) => {
    const [roomMap, setRoomMap] = useState([]); // Stores room data
    const [error, setError] = useState(null);   // Stores error state
    const [roomConfig, setRoomConfig] = useState([]);
    const [loading, setLoading] = useState(false); // Tracks the loading state
    const [isLoggedIn, setIsLoggedIn] = useState(false); // Track if the user is logged in

    useEffect(() => {
        // Always trigger fetchRooms on mount (page load)
        fetchRooms();
    }, []); // Empty dependency array ensures this runs only once after mounting

    useEffect(() => {
        if (isLoggedIn) {
            fetchRooms();
        }
    }, [isLoggedIn]); // Trigger when `isLoggedIn` changes

    // Function to fetch the room data
    const fetchRooms = async () => {
        try {
            setLoading(true); // Start loading
            const locationKeys = Object.keys(process.env)
                .filter((key) => key.startsWith("REACT_APP_LOCATION_") && !key.startsWith("REACT_APP_LOCATION_PATH_"))
                .map((key) => key.replace("REACT_APP_LOCATION_", ""));

            const rooms = [];
            const roomConfigs = [];
            for (const locationKey of locationKeys) {
                const locationName = process.env[`REACT_APP_LOCATION_${locationKey}`];
                const apiPath = process.env[`REACT_APP_LOCATION_PATH_${locationKey}`];

                if (!apiPath) {
                    console.error(`API path for ${locationName} (${locationKey}) is not defined.`);
                    continue;
                }

                try {
                    const response = await fetchWithAuth(`${apiPath}/api/config`);
                    if (response.ok) {
                        const body = await response.text();
                        const streams = parseStreams(body);
                        const configArray = body.split("\n").map((line) => line.replace(/\s+/g, ""));

                        const localRoomMap = new Map();
                        configArray.forEach((entry) => {
                            const splits = entry.split("_");
                            if (splits.length > 1 && splits[0].split(":").length === 1) {
                                localRoomMap.set(splits[0], {locationKey, locationName});
                            }
                        });
                        streams.forEach((stream) => {
                            roomConfigs.push({
                                path: apiPath,
                                streamName: stream.name,
                                streamUrl: stream.url,
                                order: stream.order,
                            })
                        })

                        localRoomMap.forEach((value, key) => {
                            rooms.push({ room: key, locationKey });
                        });
                    } else {
                        console.error(`Error fetching config for ${locationName}: ${response.status}`);
                    }
                } catch (err) {
                    console.error(`Error fetching config for ${locationName}:`, err);
                    // skip without setting an error - just write the log
                }
            }
            // Local development: the room list normally comes from each venue's go2rtc server,
            // which is not reachable from a laptop. REACT_APP_DEV_ROOMS (in .env.local, never in
            // the image) lists the games-cloud config names to show instead, per location key:
            //   REACT_APP_DEV_ROOMS="GOTHERSGADE:thering,seven;VRIDSLOSE:flugten6,urbex"
            // Only fills in locations the camera servers gave nothing for.
            const devRooms = process.env.REACT_APP_DEV_ROOMS;
            if (devRooms && process.env.NODE_ENV === "development") {
                devRooms.split(";").forEach(part => {
                    const [locationKey, list] = part.split(":");
                    if (!locationKey || !list) return;
                    if (rooms.some(r => r.locationKey === locationKey.trim())) return;
                    list.split(",").map(s => s.trim()).filter(Boolean).forEach(room => {
                        rooms.push({ room, locationKey: locationKey.trim() });
                    });
                });
            }

            // Fetch display titles for all rooms in parallel
            const gamesCloudBase = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
                .replace(/^ws/, "http")
                .replace(/ws$/, "");

            const titleResults = await Promise.allSettled(
                rooms.map(({ room }) =>
                    fetchWithAuth(`${gamesCloudBase}game/${room}/config`)
                        .then(r => r.ok ? r.json() : null)
                        .then(data => ({ room, title: data?.title || null }))
                        .catch(() => ({ room, title: null }))
                )
            );

            const titleMap = {};
            titleResults.forEach(result => {
                if (result.status === "fulfilled") {
                    titleMap[result.value.room] = result.value.title;
                }
            });

            setRoomMap(rooms.map(r => ({ ...r, title: titleMap[r.room] || null })));
            setRoomConfig(roomConfigs);
        } catch (err) {
            console.error("Failed to fetch room data:", err);
            setError(`Failed to fetch room data: ${err.message}`);
        } finally {
            setLoading(false); // Loading finished
        }
    };
// Function to get all names and URLs matching a specific gameId
    const fetchStreams = (gameId) => {
        if (!gameId || !Array.isArray(roomConfig)) {
            return [];
        }
        const streams = roomConfig
            .filter((config) => config.streamName.startsWith(gameId)) // Filter by gameId
            .map((config) => ({ path: config.path, streamName: config.streamName, streamUrl: config.streamUrl, streamOrder: config.order })); // Map to desired structure
        // check if streams are started and start them if not
        return streams;
    };
    const verifyAndFetchStreamUrl = async (stream) => {

        const { path, streamName, streamUrl, streamOrder } = stream;

        try {
            const response =
                await fetchWithAuth(`${path}/api/streams?src=${streamName}`);
            const data = await response.json();
            if (data === null) {
                // Stream must be started
                const response =
                    await fetchWithAuth(`${path}/api/streams?src=${streamUrl}&name=${streamName}`, {
                        method: "PUT", // *GET, POST, PUT, DELETE, etc.
                    });
            }
            return {name: streamName, src: addToken(`${path}/stream.html?src=${streamName}&media=video+audio)`), order: streamOrder};
        } catch (err) {
            console.error(`Error verifying/activating stream ${streamName}:`, err);
        }
    };

    return (
        <GameConfig.Provider value={{ roomMap, error, loading, fetchRooms, fetchStreams, verifyAndFetchStreamUrl, isLoggedIn, setIsLoggedIn, roomConfig }}>
            {children}
        </GameConfig.Provider>
    );
};
// Custom hook to use the GameConfig
export const useGameConfig = () => {
    const context = useContext(GameConfig);
    if (!context) {
        throw new Error("useGameConfig must be used within a GameConfigProvider");
    }
    return context;
};

const parseStreams = (fileContent) => {
    // 1. Find the `streams:` block using a regular expression.
    const streamsSection = fileContent.match(/^\s*streams:\s*$(\n^(?!\s*streams:).*?:.*?$)+/m);
    if (!streamsSection) {
        console.error("No streams block found in the file content.");
        return [];
    }

    // 2. Extract the lines following the `streams:` keyword.
    const streamsBlock = streamsSection[0].trim();

    // 3. Split by new lines to get each stream line individually.
    const lines = streamsBlock.split("\n");

    // 4. Parse each line to extract the stream `name` and `url`.
    const streams = [];

    lines.forEach((line, index) => {
        line = line.trim();
        if (!line.includes(":")){
            return;
        }
        const [name, url] = line.split(/:\s*(.+)/); // Split on the first `:`
        const [gameId, rest] = name.split(/_\s*(.+)/)
        if (name !== "" && url !== "" && url !== undefined) {
            streams.push({gameId, name, url, order: index});
        }
    })

    return streams;
};